import { graphql } from "gatsby";
import PropTypes from "prop-types";
import React, { ReactElement } from "react";
import Helmet from "react-helmet";

import { Container, Typography } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";

import Content, { HTMLContent } from "../components/Content";
import Layout from "../components/Layout";
import { TeamPageTemplate } from "./team-page";

import PreviewCompatibleImage from "../components/PreviewCompatibleImage";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "grid",
      gap: `${theme.spacing(2)}px`,
      gridArea: "content",
      paddingBottom: theme.spacing(4),
      paddingTop: theme.spacing(4),
      width: "100vw",
      "& img": {
        width: "100%",
      },
    },
    heading: {
      fontSize: "16pt",
      fontWeight: "bold"
    },
    subheading: {
      fontSize: "14pt",
      fontWeight: "bold"
    },
  })
);

export const AboutPageTemplate = ({
  title,
  heading,
  subheading,
  featuredimage,
  imagewidth,
  content,
  contentComponent,
  helmet
}): ReactElement => {
  const PageContent = contentComponent || Content;
  const classes = useStyles();
  return (
    <Container component="section">
      {helmet || ""}
      <Typography component="h1" variant="h4" className={classes.heading}>
        {heading}
      </Typography>
      {featuredimage ? (
        <PreviewCompatibleImage
          imageInfo={{
            image: featuredimage,
            alt: subheading,
            style: {
              width: `${imagewidth}px`,
              minWidth: '370px',
              maxWidth: '1024px',
              margin: "1rem",
            }
          }}
        />
      ) : null}
      <Typography
        component="h1"
        className={classes.subheading}
      >
        {subheading}
      </Typography>
      <PageContent className="content" content={content} />
    </Container>
  );
};

AboutPageTemplate.propTypes = {
  title: PropTypes.string.isRequired,
  content: PropTypes.string,
  contentComponent: PropTypes.func,
  helmet: PropTypes.object,
  heading: PropTypes.string,
  subheading: PropTypes.string,
  featuredimage: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  imagewidth: PropTypes.string
};

const AboutPage = ({ data, location }): ReactElement => {
  const { markdownRemark: post } = data;
  const classes = useStyles();
  const rootUrl = "https://williamslynch.co.uk";

  return (
    <Layout pathname={location.pathname}>
      <Container className={classes.root} component="main" fixed>
        <AboutPageTemplate
          contentComponent={HTMLContent}
          title={post.frontmatter.title}
          heading={post.frontmatter.heading}
          featuredimage={post.frontmatter.featuredimage}
          subheading={post.frontmatter.subheading}
          content={post.html}
          helmet={
            <Helmet titleTemplate="%s">
              <title>{`${post.frontmatter.title}`}</title>
              <meta name="description" content={`${post.frontmatter.description}`} />
              <meta name="image" content={`${rootUrl}${post.frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
              <meta name="og:image" content={`${rootUrl}${post.frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
              <meta name="twitter:image" content={`${rootUrl}${post.frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
            </Helmet>
            }
        />
        <TeamPageTemplate
          description={post.frontmatter.description}
          members={post.frontmatter.members}
        />
      </Container>
    </Layout>
  );
};

AboutPage.propTypes = {
  data: PropTypes.object.isRequired,
  location: PropTypes.object,
};

export default AboutPage;

export const pageQuery = graphql`
  query AboutPage($id: String!) {
    markdownRemark(id: { eq: $id }) {
      html
      frontmatter {
        title
        featuredimage {
          childImageSharp {
            fluid(maxWidth: 960, quality: 64) {
              ...GatsbyImageSharpFluid
            }
          }
        }
        imagewidth
        heading
        subheading
        description
        members {
          featuredimage {
            childImageSharp {
              fluid(maxWidth: 640, quality: 64) {
                ...GatsbyImageSharpFluid
              }
            }
          }
          name
          title
          paragraphs
        }
      }
    }
    # members: allMarkdownRemark(
    #   limit: 12
    #   sort: { order: DESC, fields: [frontmatter___date] }
    #   filter: { frontmatter: { templateKey: { eq: "team-member-post" } } }
    # ) {
    #   edges {
    #     node {
    #       excerpt(pruneLength: 400)
    #       id
    #       fields {
    #         slug
    #       }
    #       frontmatter {
    #         heading
    #         subheading
    #         description
    #         members {
    #           image {
    #             childImageSharp {
    #               fluid(maxWidth: 640, quality: 64) {
    #                 ...GatsbyImageSharpFluid
    #               }
    #             }
    #           }
    #           name
    #           title
    #           text
    #         }
    #       }
    #     }
    #   }
    # }
  }
`;
